"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { Card } from "@myapp/ui/card";
import { Badge } from "@myapp/ui/badge";
import { Button } from "@myapp/ui/button";
import { Separator } from "@myapp/ui/separator";
import { Heart, ExternalLink, Star } from "lucide-react";
import { clsx } from "clsx";
import type { ProductCardProps } from "@/types/product";
import { formatKRWSimple, getTagColor, formatRating } from "@/utils/format";
import { usePrefersReducedMotion } from "@/hooks/use-accessibility";

export function ProductCard({ 
  item, 
  showFavorite = false, 
  onFavoriteToggle,
  className 
}: ProductCardProps) {
  const [isFavorited, setIsFavorited] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFavorited(!isFavorited);
    onFavoriteToggle?.(item.id);
  };

  const handleBuyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(item.buyUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Card className={clsx(
      "group relative overflow-hidden",
      !prefersReducedMotion && "transition-all duration-200 hover:-translate-y-1",
      "hover:shadow-lg",
      "focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2",
      className
    )}
    role="article"
    aria-label={`상품 ${item.name}, 가격 ${formatKRWSimple(item.price)}, ${item.region}의 ${item.storeName}`}>
      {/* 이미지 영역 */}
      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
        {!imageError ? (
          <Image
            src={item.imageUrl}
            alt={item.name}
            fill
            className={clsx(
              "object-cover transition-transform duration-300 group-hover:scale-105",
              imageLoading && "bg-muted animate-pulse"
            )}
            onLoad={() => setImageLoading(false)}
            onError={() => {
              setImageError(true);
              setImageLoading(false);
            }}
            sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"
          />
        ) : (
          <div className="flex items-center justify-center h-full bg-muted text-muted-foreground">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-2 bg-muted-foreground/20 rounded-lg" />
              <p className="text-sm">이미지 없음</p>
            </div>
          </div>
        )}

        {/* 태그 배지 */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-1 max-w-[calc(100%-6rem)]">
          {item.tags?.slice(0, 3).map((tag) => (
            <Badge
              key={tag}
              variant="secondary"
              className={clsx(
                "text-xs font-medium border backdrop-blur-sm",
                getTagColor(tag)
              )}
            >
              {tag}
            </Badge>
          ))}
        </div>

        {/* 즐겨찾기 버튼 */}
        {showFavorite && (
          <button
            onClick={handleFavoriteClick}
            className={clsx(
              "absolute top-3 right-3 p-2 rounded-full transition-all duration-200",
              "bg-white/90 hover:bg-white shadow-sm backdrop-blur-sm",
              "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
              isFavorited && "text-red-500"
            )}
            aria-label={isFavorited ? "즐겨찾기 제거" : "즐겨찾기 추가"}
          >
            <Heart className={clsx("w-4 h-4", isFavorited && "fill-current")} />
          </button>
        )}

        {/* 제철 표시 */}
        {item.isInSeason && (
          <div className="absolute top-3 right-3 bg-primary text-primary-foreground px-2 py-1 rounded-full text-xs font-medium">
            제철
          </div>
        )}
      </div>

      {/* 콘텐츠 영역 */}
      <div className="p-4 space-y-3">
        {/* 상품명 */}
        <div className="space-y-1">
          <h3 className="font-semibold text-card-foreground line-clamp-2 leading-snug">
            {item.name}
          </h3>
          {item.farmName && (
            <p className="text-sm text-muted-foreground">{item.farmName}</p>
          )}
        </div>

        {/* 평점 및 리뷰 */}
        {item.rating && (
          <div className="flex items-center gap-2 text-sm">
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              <span className="font-medium">{item.rating.toFixed(1)}</span>
            </div>
            {item.reviewCount && (
              <span className="text-muted-foreground">
                ({item.reviewCount.toLocaleString()})
              </span>
            )}
          </div>
        )}

        {/* 가격 */}
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-bold text-primary">
            {formatKRWSimple(item.price)}
          </span>
        </div>

        {/* 설명 */}
        {item.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {item.description}
          </p>
        )}

        <Separator className="my-3" />

        {/* 액션 버튼 */}
        <div className="flex gap-2">
          <Link 
            href={`/products/${item.id}`}
            className="flex-1"
          >
            <Button 
              variant="outline" 
              size="sm"
              className="w-full"
            >
              자세히 보기
            </Button>
          </Link>
          
          <Button
            onClick={handleBuyClick}
            size="sm"
            className="flex-1 gap-1"
          >
            <ExternalLink className="w-3 h-3" />
            구매하기
          </Button>
        </div>
      </div>
    </Card>
  );
}